/**
 * 动画观察器工具模块
 * 使用 IntersectionObserver API 实现滚动触发动画
 * 
 * @module common/utils/animationObserver
 * @author wei<innereyecw@gmail.com>
 * @version 5.0.0
 */
(function (global) {
  "use strict";

  // 确保命名空间存在
  if (!global.watashi) {
    global.watashi = {};
  }
  if (!global.watashi.common) {
    global.watashi.common = {};
  }

  const common = global.watashi.common;
  const logger = (global.watashi.core && global.watashi.core.logger) || console;

  /**
   * 初始化动画观察器
   * 当带有指定类的元素进入视窗时，自动启动 CSS 动画
   * 
   * @param {Object} [options] - 配置选项
   * @param {string} [options.selector='.element-animation-paused'] - 目标元素选择器
   * @param {number} [options.threshold=0.1] - 触发阈值（0-1），元素可见比例
   * @param {string} [options.rootMargin='0px'] - 根边距，调整触发区域
   * @param {Function} [options.onVisible] - 元素可见时的回调函数
   * 
   * @example
   * // 基本用法
   * watashi.common.initAnimationObserver();
   * 
   * @example
   * // 自定义配置
   * watashi.common.initAnimationObserver({
   *   selector: '.my-animation',
   *   threshold: 0.2,
   *   rootMargin: '-50px',
   *   onVisible: function(element) {
   *     console.log('元素可见:', element);
   *   }
   * });
   * 
   * @example
   * // HTML 中使用
   * <div class="element-animation-paused" style="animation: fadeIn 1s ease;">
   *   这个元素会在进入视窗时淡入
   * </div>
   */
  common.initAnimationObserver = function (options) {
    const defaults = {
      selector: '.element-animation-paused',
      threshold: 0.1,
      rootMargin: '0px',
      onVisible: null
    };
    const config = {...defaults, ...options};

    // 检查浏览器支持
    if (!('IntersectionObserver' in window)) {
      logger.warn('[AnimationObserver] 浏览器不支持 IntersectionObserver，跳过动画初始化');
      // 降级方案：直接显示所有元素
      const elements = document.querySelectorAll(config.selector);
      elements.forEach(el => {
        el.style.animationPlayState = 'running';
        el.dataset.animationState = 'running';
      });
      return;
    }

    const elements = document.querySelectorAll(config.selector);

    // 如果没有需要观察的元素，直接返回
    if (elements.length === 0) {
      logger.log('[AnimationObserver] 未找到需要观察的元素');
      return;
    }

    // 创建交叉观察器实例
    const observer = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        // 当元素进入可视区域时
        if (entry.isIntersecting) {
          // 启动 CSS 动画
          entry.target.style.animationPlayState = 'running';
          // 记录动画状态（可用于调试或其他逻辑）
          entry.target.dataset.animationState = 'running';
          
          // 执行自定义回调
          if (typeof config.onVisible === 'function') {
            try {
              config.onVisible(entry.target);
            } catch (error) {
              logger.error('[AnimationObserver] 回调函数执行失败:', error);
            }
          }
          
          // 停止观察此元素，避免重复触发
          observer.unobserve(entry.target);
        }
      });
    }, {
      // 配置项：以视窗边缘为基准，不设置边距
      rootMargin: config.rootMargin,
      // 当元素至少有指定比例进入视窗时触发
      threshold: config.threshold
    });

    // 开始观察所有需要动画的元素
    elements.forEach(function (element) {
      observer.observe(element);
    });

    logger.log(`[AnimationObserver] 已观察 ${elements.length} 个元素`);
  };

})(typeof window !== "undefined" ? window : this);
